import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
	ElementRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import {
	AUDIT_LOG_COLUMN_DATE_TIME,
	AUDIT_LOG_COLUMN_PERFORMED_BY_NAME,
	AUDIT_LOG_COLUMN_PERFORMED_BY_COMPANY,
	AUDIT_LOG_COLUMN_USER,
	AUDIT_LOG_COLUMN_COMPANY,
	AUDIT_LOG_COLUMN_DEVICE,
	AUDIT_LOG_COLUMN_DEVICE_GROUP,
	AUDIT_LOG_COLUMN_PROPERTY,
	AUDIT_LOG_COLUMN_DETAILS,
} from 'src/app/app.constants';
import {
	AuditLogFilterDialogComponent,
	AuditLogFilters,
} from '../audit-log-filter-dialog/audit-log-filter-dialog.component';
import { BreadcrumbLabelDirective } from '../../directives/breadcrumb-label.directive';
import { MatIcon } from '@angular/material/icon';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../components/data-set/data-set.component';
import { DataSetInfiniteScrollViewComponent } from '../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { DatePipe } from '@angular/common';
import {
	ShortTimezonePipe,
	AuditLogDetailsPipe,
	ShouldShowFormattedChipPipe,
	DeviceControlDaysPipe,
	AccessDatePipe,
} from '../../components/pipes';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { DataSetLoadingRowComponent } from '../../components/data-set/data-set-loading-row.component';

type AuditLogEntry = Components.Schemas.AuditReportRecord;
type DeviceControl = Components.Schemas.DeviceControl;

interface FilterChip {
	id: string;
	text: string;
}

// Simple Chip Display Component for role audit records
@Component({
	selector: 'app-audit-filter-chips',
	template: `
		<div class="chip-display-section">
			<div class="chips-container">
				<mat-chip-set class="custom-chip-set">
					@for (chip of chips; track chip.id) {
						<mat-chip
							class="custom-filter-chip"
							[attr.data-automation-id]="'filter-chip-' + chip.id"
						>
							<span class="chip-text">{{ chip.text }}</span>
							<mat-icon class="chip-checkmark">check</mat-icon>
						</mat-chip>
					}
				</mat-chip-set>
			</div>
		</div>
	`,
	styleUrls: ['./audit-log-list.component.scss'],
	imports: [MatChipSet, MatChip, MatIcon],
})
export class AuditFilterChipsComponent {
	@Input() chips: FilterChip[] = [];
}

// Chip for showing granted/edited access changes and types
@Component({
	selector: 'app-formatted-chip',
	template: `
		<div class="formatted-chip-wrapper">
			<!-- Description with Sub-Authorization Badge on same line -->
			<div class="description-line">
				<!-- Description -->
				<ng-content></ng-content>
				<!-- Sub-Authorization Badge -->
				@if (controlAssignment?.allowSubAuthorization) {
					<span class="sub-authorization-badge">
						<span class="badge-text" i18n="@@auditLog-subAuthorization"
							>Sub-Authorization</span
						>
						<mat-icon class="badge-checkmark">check</mat-icon>
					</span>
				}
			</div>

			<!-- Chip bubble on separate line below -->
			<div class="custom-formatted-chip">
				<!-- Days and Time Section -->
				@if (deviceControl) {
					<span class="chip-section">
						<span class="chip-text">{{
							deviceControl | deviceControlDays
						}}</span>
					</span>
				}

				<!-- Date Range and Access Types grouped together -->
				<span class="chip-section-group">
					<!-- Date Range Section -->
					@if (controlAssignment?.utcStartDate) {
						<span class="chip-section">
							<span class="chip-text chip-text-date-range">
								<span>{{ controlAssignment.utcStartDate | accessDate }}</span>
								<span> - </span>
								@if (controlAssignment.utcEndDate) {
									<span>{{ controlAssignment.utcEndDate | accessDate }}</span>
								} @else {
									<span i18n="@@auditLog-noEndDate">None</span>
								}
							</span>
						</span>
					}

					<!-- Access Types Section -->
					@if (accessTypes && accessTypes.length > 0) {
						<span class="chip-section">
							@for (accessType of accessTypes; track accessType.tooltip) {
								<mat-icon
									class="chip-icon access-type-icon"
									[svgIcon]="accessType.icon"
									[title]="accessType.tooltip"
								>
								</mat-icon>
							}
						</span>
					}
				</span>
			</div>
		</div>
	`,
	styleUrls: ['./audit-log-list.component.scss'],
	imports: [MatIcon, DeviceControlDaysPipe, AccessDatePipe],
})
export class FormattedChipComponent {
	@Input() deviceControl?: DeviceControl;
	@Input() controlAssignment?: any;
	@Input() accessTypes?: AccessType[];
}

interface AccessType {
	icon: string; // Material icon name
	tooltip: string; // Tooltip text
}

// "Audit Log" page
@Component({
	selector: 'app-audit-log-list',
	templateUrl: './audit-log-list.component.html',
	styleUrl: './audit-log-list.component.scss',
	imports: [
		BreadcrumbLabelDirective,
		MatIcon,
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetInfiniteScrollViewComponent,
		MatProgressSpinner,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		FormattedChipComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DatePipe,
		ShortTimezonePipe,
		AuditLogDetailsPipe,
		ShouldShowFormattedChipPipe,
		DataSetLoadingRowComponent,
	],
})
export class AuditLogListComponent implements AfterViewInit, OnDestroy, OnInit {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild('tableContainer', { read: ElementRef })
	tableContainer!: ElementRef;
	@ViewChild('filtersButton', { read: ElementRef }) filtersButton!: ElementRef;
	@Input() deviceId?: string;
	@Input() userId?: string;
	@Input() companyId?: string;
	@Input() propertyId?: string;
	@Input() deviceGroupId?: string;
	@Input() roleId?: string;
	@Input() clientId?: string;
	@Input() requestingCompanyId?: string;
	@Input() appBreadcrumbLabel?: string;
	@Input() hiddenColumns: string[] = [];

	displayedColumns: string[] = [];
	allColumns: string[] = []; // Store all available columns

	dataSource = new MatTableDataSource<AuditLogEntry>();
	filterSubscription?: Subscription;

	// Store secondary filter dialog UI state (rich objects with names, emails, etc. for display)
	secondaryFilterDialogState?: AuditLogFilters;

	// Store compiled secondary filter query params (for API calls, infinite scroll, and sorting)
	secondaryFilterQueryParams: QueryParam[] = [];

	breadcrumbLabel = $localize`:@@auditLogList-breadcrumbLabel2:Audit Log`;

	// Track clicked field name for sub-breadcrumb
	clickedField: string | null = null;
	// Track clicked column type to hide it
	clickedColumnType: string | null = null;

	// Loading keys for audit log API endpoints
	generateAuditLogLoadingKey = ApiLoadingKeys.generateAuditReport;
	generateAuditLogCsvLoadingKey = ApiLoadingKeys.generateAuditReportCsv;
	finishedLoadingInitData: boolean = false;

	// Infinite scroll properties
	private readonly initialRowCount = 30;
	private readonly incrementRowCount = 30;
	currentRowCount = 30;
	isLoadingMore = false;
	hasMoreData = true;
	lastFilterSnapshot: QueryParam[] = [];
	lastBatchSize = 0; // Track size of last response to prevent unnecessary API calls

	constructor(
		private readonly dialog: MatDialog,
		private readonly apiService: ApiService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService,
		private readonly route: ActivatedRoute
	) {}

	ngOnInit(): void {
		// Initialize default secondary filters (1 month date range, no end date)
		this.initializeDefaultSecondaryFilters();

		this.allColumns.push(AUDIT_LOG_COLUMN_DATE_TIME);
		this.allColumns.push(AUDIT_LOG_COLUMN_PERFORMED_BY_NAME);
		this.allColumns.push(AUDIT_LOG_COLUMN_PERFORMED_BY_COMPANY);
		this.allColumns.push(AUDIT_LOG_COLUMN_USER);
		this.allColumns.push(AUDIT_LOG_COLUMN_COMPANY);
		this.allColumns.push(AUDIT_LOG_COLUMN_DEVICE);
		this.allColumns.push(AUDIT_LOG_COLUMN_DEVICE_GROUP);
		this.allColumns.push(AUDIT_LOG_COLUMN_PROPERTY);
		this.allColumns.push(AUDIT_LOG_COLUMN_DETAILS);
		// setup all of the columns to be displayed and exclude anything that the parent component said should be hidden
		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_DATE_TIME)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_DATE_TIME);
		}

		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_PERFORMED_BY_NAME)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_PERFORMED_BY_NAME);
		}

		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_PERFORMED_BY_COMPANY)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_PERFORMED_BY_COMPANY);
		}

		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_USER)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_USER);
		}

		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_COMPANY)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_COMPANY);
		}

		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_DEVICE)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_DEVICE);
		}

		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_DEVICE_GROUP)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_DEVICE_GROUP);
		}

		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_PROPERTY)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_PROPERTY);
		}

		if (!this.hiddenColumns.includes(AUDIT_LOG_COLUMN_DETAILS)) {
			this.displayedColumns.push(AUDIT_LOG_COLUMN_DETAILS);
		}
	}

	/**
	 * Update displayed columns to hide the clicked column
	 */
	updateDisplayedColumns(columnToHide: string): void {
		this.clickedColumnType = columnToHide;
		this.displayedColumns = this.allColumns.filter(
			(col) => col !== columnToHide
		);
	}

	ngAfterViewInit(): void {
		this.dataSet?.applyFilters();
		this.filterSubscription = this.dataSet?.queryParams
			.pipe(
				mergeMap(async (filter) => {
					// Reset infinite scroll state when filters change
					const currentFilters = this.buildQueryParams(filter);
					const filtersChanged =
						JSON.stringify(currentFilters) !==
						JSON.stringify(this.lastFilterSnapshot);

					if (filtersChanged) {
						this.resetInfiniteScroll();
						this.lastFilterSnapshot = currentFilters;
					}

					const result = await this.fetchAuditReport({
						baseFilters: filter,
						includeSecondaryFilters: true,
						rowIndex: 0,
						rowCount: this.currentRowCount,
					});

					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((result) => {
				// Replace data on filter change, append on load more
				if (this.currentRowCount === this.initialRowCount) {
					this.dataSource.data = result.data;
				} else {
					this.dataSource.data = [...this.dataSource.data, ...result.data];
				}

				this.isLoadingMore = false;
			});
	}

	/**
	 * Initialize default secondary filters (1 month date range)
	 */
	initializeDefaultSecondaryFilters(): void {
		const oneMonthAgo = new Date();
		oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
		oneMonthAgo.setHours(0, 0, 0, 0);

		this.secondaryFilterQueryParams = [
			{
				param: 'secondaryFilterUtcStartDate',
				value: oneMonthAgo.toISOString(),
			},
		];
	}

	/**
	 * Add primary context filters (deviceId, userId, companyId, etc.) to query params
	 */
	addPrimaryFilters(queryParams: QueryParam[]): void {
		if (this.deviceId) {
			queryParams.push({ param: 'deviceId', value: this.deviceId });
		}
		if (this.userId) {
			queryParams.push({ param: 'userId', value: this.userId });
		}
		if (this.companyId) {
			queryParams.push({ param: 'companyId', value: this.companyId });
		}
		if (this.propertyId) {
			queryParams.push({ param: 'propertyId', value: this.propertyId });
		}
		if (this.deviceGroupId) {
			queryParams.push({ param: 'deviceGroupId', value: this.deviceGroupId });
		}
		if (this.roleId) {
			queryParams.push({ param: 'roleId', value: this.roleId });
		}
		if (this.clientId) {
			queryParams.push({ param: 'clientId', value: this.clientId });
		}
	}

	buildQueryParams(filter: QueryParam[]): QueryParam[] {
		const queryParams: QueryParam[] = [...filter];
		this.addPrimaryFilters(queryParams);
		return queryParams;
	}

	/**
	 * Fetch audit report data with all necessary filters and enrichment
	 * Consolidates query building, API call, duplicate filtering, and infinite scroll state management
	 */
	async fetchAuditReport(options: {
		baseFilters?: QueryParam[];
		includePrimaryFilters?: boolean;
		includeSecondaryFilters?: boolean;
		includeRequestingCompanyId?: boolean;
		rowIndex?: number;
		rowCount?: number;
		updateInfiniteScrollState?: boolean;
		resetInfiniteScroll?: boolean; // Reset scroll state for new filters
		existingData?: Components.Schemas.AuditReportRecord[]; // For filtering duplicates in infinite scroll
	}): Promise<{
		data: Components.Schemas.AuditReportRecord[];
		queryParams: QueryParam[];
	}> {
		const {
			baseFilters = [],
			includePrimaryFilters = true,
			includeSecondaryFilters = true,
			includeRequestingCompanyId = true,
			rowIndex = 0,
			rowCount = this.currentRowCount,
			updateInfiniteScrollState = true,
			resetInfiniteScroll = false,
			existingData,
		} = options;

		// Reset infinite scroll state if requested
		if (resetInfiniteScroll) {
			this.currentRowCount = this.initialRowCount;
			this.hasMoreData = true;
			this.lastBatchSize = 0;
		}

		// Build query params
		// Filter out pagination params from baseFilters since we'll add them fresh below
		const queryParams: QueryParam[] = baseFilters.filter(
			(p) => p.param !== 'rowIndex' && p.param !== 'rowCount'
		);

		// Add primary context filters
		if (includePrimaryFilters) {
			this.addPrimaryFilters(queryParams);
		}

		// Add secondary filters if requested
		if (includeSecondaryFilters) {
			queryParams.push(...this.secondaryFilterQueryParams);
		}

		// Add pagination params (always add fresh to ensure correct values)
		queryParams.push({ param: 'rowIndex', value: rowIndex });
		queryParams.push({ param: 'rowCount', value: rowCount });

		// Add requestingCompanyId
		if (includeRequestingCompanyId) {
			const requestingCompanyId = await this.determineRequestingCompanyId();
			if (requestingCompanyId) {
				queryParams.push({
					param: 'requestingCompanyId',
					value: requestingCompanyId,
				});
			}
		}

		// Call API and enrich response
		const result = await this.apiService.generateAuditReport(queryParams);

		let enrichedData = result.map((row) => ({
			...row,
			accessTypes: this.getAccessTypesFromSnapshot(row),
			deviceControl: this.getDeviceControlFromSnapshot(row),
			controlAssignment: this.getControlAssignmentFromSnapshot(row),
		}));

		// Filter duplicates if existing data provided (for infinite scroll)
		if (existingData) {
			const existingIds = new Set(existingData.map((row) => this.rowId(row)));
			enrichedData = enrichedData.filter(
				(row) => !existingIds.has(this.rowId(row))
			);
		}

		// Update infinite scroll state if requested
		if (updateInfiniteScrollState) {
			this.lastBatchSize = enrichedData.length;
			// For infinite scroll: check if we got a full increment of new data
			// If we're at initial load (rowCount === initialRowCount), check against initialRowCount
			// Otherwise, check if new data equals incrementRowCount (the amount we just added)
			const expectedNewDataSize =
				rowCount === this.initialRowCount
					? this.initialRowCount
					: this.incrementRowCount;
			this.hasMoreData = enrichedData.length >= expectedNewDataSize;
			this.lastFilterSnapshot = queryParams;
		}

		return { data: enrichedData, queryParams };
	}

	onScroll(event: any): void {
		if (this.isLoadingMore || !this.hasMoreData) {
			return;
		}

		const element = event.target;
		const scrollHeight = element.scrollHeight;
		const scrollTop = element.scrollTop;
		const clientHeight = element.clientHeight;
		const distanceFromBottom = scrollHeight - scrollTop;
		const threshold = clientHeight + 100;
		const atBottom = distanceFromBottom <= threshold;

		if (atBottom) {
			// Only load more data if the previous response had a full page of results (or close to it)
			const expectedPageSize =
				this.currentRowCount === this.initialRowCount
					? this.initialRowCount
					: this.incrementRowCount;

			if (this.lastBatchSize >= expectedPageSize) {
				this.loadMoreData();
			}
		}
	}

	async loadMoreData(): Promise<void> {
		if (this.isLoadingMore || !this.hasMoreData) {
			return;
		}

		this.isLoadingMore = true;

		// Save current scroll position
		const container = this.tableContainer?.nativeElement;
		const previousScrollTop = container?.scrollTop || 0;

		this.currentRowCount += this.incrementRowCount;

		try {
			const result = await this.fetchAuditReport({
				baseFilters: this.lastFilterSnapshot,
				includePrimaryFilters: false, // Primary filters already in lastFilterSnapshot
				includeSecondaryFilters: false, // Secondary filters already in lastFilterSnapshot
				includeRequestingCompanyId: false, // requestingCompanyId already in lastFilterSnapshot
				rowIndex: 0,
				rowCount: this.currentRowCount,
				updateInfiniteScrollState: true,
				existingData: this.dataSource.data, // Filter duplicates
			});

			this.dataSource.data = [...this.dataSource.data, ...result.data];

			// Wait for Angular to render the new data, then restore scroll position
			setTimeout(() => {
				if (container) {
					// Simply restore the previous scroll position
					// The browser will keep the user at the same visual position
					container.scrollTop = previousScrollTop;
				}
			}, 0);
		} catch (error) {
			console.error('[loadMoreData] Error loading more data:', error);
			this.currentRowCount -= this.incrementRowCount;
		} finally {
			this.isLoadingMore = false;
		}
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	onRefineFiltersClicked(): void {
		// Combine static hidden columns with dynamically clicked column
		let hiddenColumnsForDialog = [...this.hiddenColumns];
		if (
			this.clickedColumnType &&
			!hiddenColumnsForDialog.includes(this.clickedColumnType)
		) {
			hiddenColumnsForDialog = [this.clickedColumnType];
		}

		const dialogRef = this.dialog.open(AuditLogFilterDialogComponent, {
			panelClass: 'drawer-dialog',
			data: {
				reportFilters: this.secondaryFilterDialogState,
				hiddenColumns: hiddenColumnsForDialog,
				deviceId: this.deviceId,
				userId: this.userId,
				companyId: this.companyId,
				propertyId: this.propertyId,
				deviceGroupId: this.deviceGroupId,
				roleId: this.roleId,
				clientId: this.clientId,
			},
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			// Remove focus from the button after dialog closes
			setTimeout(() => {
				if (this.filtersButton?.nativeElement) {
					const buttonElement =
						this.filtersButton.nativeElement.querySelector('button');
					if (buttonElement) {
						buttonElement.blur();
					}
				}
			}, 0);

			if (result) {
				this.secondaryFilterDialogState = result.filters;
				// Apply the compiled filters by triggering a refresh with the new filters
				await this.applySecondaryFilters(result.compiledFilters);
			}
		});
	}

	/**
	 * Reset infinite scroll state to initial values
	 */
	private resetInfiniteScroll(): void {
		this.currentRowCount = this.initialRowCount;
		this.hasMoreData = true;
		this.lastBatchSize = 0;
	}

	/**
	 * Reset secondary filters to default (1 month date range) and clear dialog UI state
	 */
	resetSecondaryFilters(): void {
		// Reset secondary filter query params to default (1 month date range)
		this.initializeDefaultSecondaryFilters();
		// Clear the filter dialog UI state so it shows empty selections when reopened
		this.secondaryFilterDialogState = undefined;
	}

	/**
	 * Apply secondary filters from the filter dialog
	 */
	async applySecondaryFilters(secondaryFilters: QueryParam[]): Promise<void> {
		// Store secondary filter query params for use in infinite scroll and sorting
		this.secondaryFilterQueryParams = [...secondaryFilters];

		// Reset infinite scroll state before fetching
		this.resetInfiniteScroll();

		// Clear table data to show loading state
		this.dataSource.data = [];
		this.finishedLoadingInitData = false;

		// Fetch data with new secondary filters
		const result = await this.fetchAuditReport({
			baseFilters: [],
			includePrimaryFilters: true,
			includeSecondaryFilters: true,
			includeRequestingCompanyId: true,
			rowIndex: 0,
			rowCount: this.currentRowCount,
			updateInfiniteScrollState: true,
		});

		this.dataSource.data = result.data;
		this.scrollToTop();
		this.finishedLoadingInitData = true;
	}

	async onDownloadClicked(): Promise<void> {
		// Check if secondary filters contain any filter other than secondaryFilterUtcStartDate
		const hasOtherSecondaryFilters = this.secondaryFilterQueryParams.some(
			(param) => param.param != 'secondaryFilterUtcStartDate'
		);

		if (!hasOtherSecondaryFilters) {
			// Open the filter dialog instead of downloading
			this.onRefineFiltersClicked();
			return;
		}

		// Get current filters by taking the latest value from the observable
		this.dataSet?.queryParams
			.pipe(take(1))
			.subscribe(async (currentFilters) => {
				const filteredParams = currentFilters.filter(
					(param) =>
						param.param !== 'rowIndex' &&
						param.param !== 'rowCount' &&
						param.param !== 'sortBy' &&
						param.param !== 'sortDirection'
				);
				const queryParams: QueryParam[] = [...filteredParams];

				// Add primary context filters
				this.addPrimaryFilters(queryParams);

				// Add secondary filters
				queryParams.push(...this.secondaryFilterQueryParams);

				// Determine requestingCompanyId based on entity context
				const requestingCompanyId = await this.determineRequestingCompanyId();
				if (requestingCompanyId) {
					queryParams.push({
						param: 'requestingCompanyId',
						value: requestingCompanyId,
					});
				}

				const fileName = 'audit-log-report.csv';
				this.apiService.generateAuditReportCsv(fileName, queryParams);
			});
	}

	rowId(row: AuditLogEntry): string {
		return row?.id || 'unknown-id';
	}

	getPerformedByName(row: AuditLogEntry): string {
		const name =
			`${row.performedByFirstName || ''} ${row.performedByLastName || ''}`.trim();
		return name || '- - -';
	}

	getUserName(row: AuditLogEntry): string {
		const name = `${row.userFirstName || ''} ${row.userLastName || ''}`.trim();
		return name || '- - -';
	}

	/**
	 * Scroll the table container to the top
	 */
	private scrollToTop(): void {
		const container = this.tableContainer?.nativeElement;
		if (container) {
			container.scrollTop = 0;
		}
	}

	/**
	 * Generic method to filter audit log by a specific parameter
	 * @param paramName The query parameter name (e.g., 'userId', 'deviceId')
	 * @param paramValue The value to filter by
	 * @param fieldName The field name for the sub-breadcrumb
	 * @param columnToHide The column to hide after filtering
	 */
	private async filterByParam(
		paramName: string,
		paramValue: string | number,
		fieldName: string,
		columnToHide: string
	): Promise<void> {
		// Store the clicked field name for sub-breadcrumb
		this.clickedField = fieldName;

		// Fetch data with single parameter filter
		// resetInfiniteScroll: true handles resetting currentRowCount, hasMoreData, and lastBatchSize
		// updateInfiniteScrollState: true then updates lastBatchSize with actual returned data length
		const result = await this.fetchAuditReport({
			baseFilters: [{ param: paramName, value: paramValue.toString() }],
			includePrimaryFilters: false, // Only filter by the clicked param
			includeSecondaryFilters: false, // Don't include secondary filters
			includeRequestingCompanyId: false, // Don't need requestingCompanyId for single param filter
			rowIndex: 0,
			rowCount: this.currentRowCount,
			updateInfiniteScrollState: true,
			resetInfiniteScroll: true, // Reset infinite scroll for new filter
		});

		this.dataSource.data = result.data;

		// Scroll to top after filtering
		this.scrollToTop();
		// Hide the clicked column
		this.updateDisplayedColumns(columnToHide);
	}

	/**
	 * Handle click on performed by name field to filter by userId
	 * Clears all previous filters and only queries by the clicked user
	 */
	async onPerformedByNameClicked(row: AuditLogEntry): Promise<void> {
		const userId = row.performingEntityRelationship?.childEntityId;
		if (!userId) {
			return;
		}

		await this.filterByParam(
			'userId',
			userId,
			this.getPerformedByName(row),
			AUDIT_LOG_COLUMN_PERFORMED_BY_NAME
		);
	}

	/**
	 * Handle click on user field to filter by userId
	 * Clears all previous filters and only queries by the clicked user
	 */
	async onUserClicked(row: AuditLogEntry): Promise<void> {
		const userId = row.systemAuditLog?.userId;
		if (!userId) {
			return;
		}

		await this.filterByParam(
			'userId',
			userId,
			this.getUserName(row),
			AUDIT_LOG_COLUMN_USER
		);
	}

	/**
	 * Handle click on performed by company field to filter by companyId
	 * Clears all previous filters and only queries by the clicked company
	 */
	async onPerformedByCompanyClicked(row: AuditLogEntry): Promise<void> {
		const companyId = row.performingEntityRelationship?.parentEntityId;
		if (!companyId) {
			return;
		}

		await this.filterByParam(
			'requestingCompanyId',
			companyId,
			row.performedByCompany || '',
			AUDIT_LOG_COLUMN_PERFORMED_BY_COMPANY
		);
	}

	/**
	 * Handle click on company field to filter by companyId
	 * Clears all previous filters and only queries by the clicked company
	 */
	async onCompanyClicked(row: AuditLogEntry): Promise<void> {
		const companyId = row.systemAuditLog?.companyId;
		if (!companyId) {
			return;
		}

		await this.filterByParam(
			'requestingCompanyId',
			companyId,
			row.company || '',
			AUDIT_LOG_COLUMN_COMPANY
		);
	}

	/**
	 * Handle click on device field to filter by deviceId
	 * Clears all previous filters and only queries by the clicked device
	 */
	async onDeviceClicked(row: AuditLogEntry): Promise<void> {
		const deviceId = row.systemAuditLog?.deviceId;
		if (!deviceId) {
			return;
		}

		await this.filterByParam(
			'deviceId',
			deviceId,
			row.device || '',
			AUDIT_LOG_COLUMN_DEVICE
		);
	}

	/**
	 * Handle click on device group field to filter by deviceGroupId
	 * Clears all previous filters and only queries by the clicked device group
	 */
	async onDeviceGroupClicked(row: AuditLogEntry): Promise<void> {
		const deviceGroupId = row.systemAuditLog?.deviceGroupId;
		if (!deviceGroupId) {
			return;
		}

		await this.filterByParam(
			'deviceGroupId',
			deviceGroupId,
			row.deviceGroup || '',
			AUDIT_LOG_COLUMN_DEVICE_GROUP
		);
	}

	/**
	 * Handle click on property field to filter by propertyId
	 * Clears all previous filters and only queries by the clicked property
	 */
	async onPropertyClicked(row: AuditLogEntry): Promise<void> {
		const propertyId = row.systemAuditLog?.locationId;
		if (!propertyId) {
			return;
		}

		await this.filterByParam(
			'propertyId',
			propertyId,
			row.property || '',
			AUDIT_LOG_COLUMN_PROPERTY
		);
	}

	/**
	 * Determine the requesting company ID by extracting it from the current URL route.
	 * The URL pattern can be either:
	 * 1. Path parameter: /company/{companyId}/...
	 * 2. Query parameter: /property-details/123?companyId=456
	 * This ensures audit logs are viewed from the perspective of the company in the URL.
	 *
	 * Special cases:
	 * - For properties: Skip requestingCompanyId and let the backend determine it from the property's actual companyId
	 * - For companies: Use the companyId input directly (when viewing company audit log tab)
	 *
	 * Uses the same pattern as other components: route.snapshot.params.companyId
	 * See: user-detail.component.ts, role-detail.component.ts, user-about.component.ts
	 */
	async determineRequestingCompanyId(): Promise<string | undefined> {
		// If companyId is set as an input, we're viewing a company's audit log directly
		// Use the company's own ID as the requestingCompanyId
		if (this.companyId) {
			return this.companyId;
		}

		// Skip for properties and let backend use property's actual companyId
		if (this.propertyId) {
			return undefined;
		}

		// First check query parameters (e.g., ?companyId=123)
		let currentRoute: ActivatedRoute | null = this.route;
		while (currentRoute) {
			const companyIdFromQuery = currentRoute.snapshot.queryParams.companyId;
			if (companyIdFromQuery) {
				return companyIdFromQuery;
			}
			currentRoute = currentRoute.parent;
		}

		// Then check path parameters (e.g., /company/{companyId}/...)
		currentRoute = this.route;
		while (currentRoute) {
			const companyIdFromPath = currentRoute.snapshot.params.companyId;
			if (companyIdFromPath) {
				return companyIdFromPath;
			}
			currentRoute = currentRoute.parent;
		}

		return undefined;
	}

	/**
	 * Build DeviceControl object from audit log snapshot data
	 * Maps DeviceControlSnapshot fields to the DeviceControl format used by pipes and components
	 */
	getDeviceControlFromSnapshot(row: AuditLogEntry): DeviceControl | undefined {
		const snapshot = row.deviceControlSnapshot;
		if (!snapshot?.id) return undefined;

		return {
			id: snapshot.deviceControlId,
			name: snapshot.name,
			companyId: snapshot.companyId,
			days: {
				sunday: { value: snapshot.recursSunday ?? false },
				monday: { value: snapshot.recursMonday ?? false },
				tuesday: { value: snapshot.recursTuesday ?? false },
				wednesday: { value: snapshot.recursWednesday ?? false },
				thursday: { value: snapshot.recursThursday ?? false },
				friday: { value: snapshot.recursFriday ?? false },
				saturday: { value: snapshot.recursSaturday ?? false },
			},
			startTime: snapshot.startTime,
			endTime: snapshot.endTime,
			permissions: {
				canReleaseHasp: { value: snapshot.canReleaseHasp ?? false },
				canOpenKeyDoor: { value: snapshot.canOpenKeyDoor ?? false },
				canUpdateFirmware: { value: snapshot.canUpdateFirmware ?? false },
				canInstallDevice: { value: snapshot.canInstallDevice ?? false },
				canRemoveDevice: { value: snapshot.canRemoveDevice ?? false },
				canArmDisarmAjaxAlarm: {
					value: snapshot.canArmDisarmAjaxAlarm ?? false,
				},
				canOverrideAjaxAlarm: { value: snapshot.canOverrideAjaxAlarm ?? false },
			},
		} as DeviceControl;
	}

	/**
	 * Get control assignment from snapshot
	 * Returns undefined if the snapshot doesn't exist
	 */
	getControlAssignmentFromSnapshot(row: AuditLogEntry): any {
		const snapshot = row.controlAssignmentSnapshot;
		if (!snapshot?.id) return undefined;

		return snapshot;
	}

	/**
	 * Build array of AccessType icons from control assignment and device control snapshots
	 * Returns icon names and tooltips for each enabled permission/capability
	 */
	getAccessTypesFromSnapshot(row: AuditLogEntry): AccessType[] {
		const accessTypes: AccessType[] = [];
		const controlSnapshot = row.deviceControlSnapshot;
		const assignmentSnapshot = row.controlAssignmentSnapshot;

		if (!controlSnapshot?.id) return accessTypes;

		// Device capabilities — single Device Access icon when either open-door or release-hasp is set
		if (controlSnapshot.canOpenKeyDoor || controlSnapshot.canReleaseHasp) {
			accessTypes.push({
				icon: 'sl-open-key-door',
				tooltip: $localize`:@@auditLog-deviceAccess:Device Access`,
			});
		}
		if (controlSnapshot.canInstallDevice) {
			accessTypes.push({
				icon: 'sl-install-device',
				tooltip: $localize`:@@auditLog-canInstallDevice:Install Device`,
			});
		}
		if (controlSnapshot.canRemoveDevice) {
			accessTypes.push({
				icon: 'sl-remove-device',
				tooltip: $localize`:@@auditLog-canRemoveDevice:Remove Device`,
			});
		}

		// Code generation capabilities
		if (assignmentSnapshot?.allowCodeGeneration) {
			accessTypes.push({
				icon: 'sl-generate-access-code',
				tooltip: $localize`:@@auditLog-allowCodeGeneration:Generate Access Code`,
			});
		}
		if (assignmentSnapshot?.allowHaspCodeGeneration) {
			accessTypes.push({
				icon: 'sl-generate-hasp-code',
				tooltip: $localize`:@@auditLog-allowHaspCodeGeneration:Generate Hasp Code`,
			});
		}

		// Alarm capabilities
		if (controlSnapshot.canArmDisarmAjaxAlarm) {
			accessTypes.push({
				icon: 'sl-shield-checkmark',
				tooltip: $localize`:@@auditLog-canArmDisarmAjaxAlarm:Arm/Disarm Alarm`,
			});
		}
		if (controlSnapshot.canOverrideAjaxAlarm) {
			accessTypes.push({
				icon: 'sl-bell-crossed-out',
				tooltip: $localize`:@@auditLog-canOverrideAjaxAlarm:Override Alarm`,
			});
		}

		return accessTypes;
	}
}
